import React, { useState, useEffect } from "react";
import { Navigation } from "./components/navigation";
import { Header } from "./components/header";
import { Features } from "./components/features";
import { About } from "./components/about";
import { Services } from "./components/services";
import { Gallery } from "./components/gallery";
import { Testimonials } from "./components/testimonials";
import { GlobalPresence } from "./components/global-presence";
import { Contact } from "./components/contact";
import { CaseDetail } from "./components/case-detail";
import { CompanyDetail } from "./components/company-detail";
import { Footer } from "./components/footer";
import { AgentShowcase } from "./components/agent-showcase";
import { AgentPlatformPage } from "./components/agent-platform";
import { InsightsPage } from "./components/insights";
import { InsightDetailPage } from "./components/insight-detail";
import { AdminNewsPage } from "./components/admin-news";
import JsonData from "./data/data.json";
import { useInsights } from "./hooks/use-insights";
import SmoothScroll from "smooth-scroll";
import "./App.css";

export const scroll = new SmoothScroll('a[href*="#"]', {
  speed: 1000,
  speedAsDuration: true,
});

const CASE_HASH_PREFIX = "#/case/";
const COMPANY_PAGE_SLUGS = [
  "global-presence",
  "team-expertise",
  "why-goldly",
];

const PRODUCT_PAGE_SLUGS = ["agent-platform", "insights"];
const ADMIN_NEWS_HASH = "#/admin/news";

const slugFromHash = () => {
  const h = window.location.hash || "";
  return h.startsWith(CASE_HASH_PREFIX)
    ? decodeURIComponent(h.slice(CASE_HASH_PREFIX.length))
    : null;
};

const companyPageFromHash = () => {
  const hash = window.location.hash || "";
  const slug = hash.startsWith("#/") ? hash.slice(2) : "";
  return COMPANY_PAGE_SLUGS.includes(slug) ? slug : null;
};

const productPageFromHash = () => {
  const hash = window.location.hash || "";
  const slug = hash.startsWith("#/") ? hash.slice(2) : "";
  if (PRODUCT_PAGE_SLUGS.includes(slug)) return slug;
  if (slug.startsWith("insights/")) return slug;
  return null;
};

const isAdminNewsRoute = () => window.location.hash === ADMIN_NEWS_HASH;

const App = () => {
  const [landingPageData, setLandingPageData] = useState({});
  const [activeCaseSlug, setActiveCaseSlug] = useState(() => slugFromHash());
  const [activeCompanyPage, setActiveCompanyPage] = useState(() =>
    companyPageFromHash()
  );
  const [activeProductPage, setActiveProductPage] = useState(() =>
    productPageFromHash()
  );
  const [activeAdminPage, setActiveAdminPage] = useState(() => isAdminNewsRoute());
  const { items: insights, refresh: refreshInsights } = useInsights();

  useEffect(() => {
    setLandingPageData(JsonData);
  }, []);

  useEffect(() => {
    const onHash = () => {
      setActiveCaseSlug(slugFromHash());
      setActiveCompanyPage(companyPageFromHash());
      setActiveProductPage(productPageFromHash());
      setActiveAdminPage(isAdminNewsRoute());
      window.scrollTo(0, 0);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const openCase = (slug) => {
    window.location.hash = CASE_HASH_PREFIX + slug;
  };

  const openCompanyPage = (slug) => {
    if (activeCompanyPage === slug) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    window.location.hash = `#/${slug}`;
  };

  const openProductPage = (slug) => {
    if (activeProductPage === slug) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    window.location.hash = `#/${slug}`;
  };

  const returnHome = (section = "page-top") => {
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}#${section}`
    );
    setActiveCaseSlug(null);
    setActiveCompanyPage(null);
    setActiveProductPage(null);
    setActiveAdminPage(false);
    window.requestAnimationFrame(() => {
      document
        .getElementById(section)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const cases = landingPageData.Testimonials || [];
  const activeCase = activeCaseSlug
    ? cases.find((c) => c.slug === activeCaseSlug)
    : null;

  if (activeCase) {
    return (
      <>
        <CaseDetail
          data={activeCase}
          allCases={cases}
          onBack={returnHome}
          onOpenCase={openCase}
        />
        <Footer contact={landingPageData.Contact} onHome={returnHome} />
      </>
    );
  }

  if (activeAdminPage) {
    return (
      <AdminNewsPage
        items={insights}
        onRefresh={refreshInsights}
        onExit={() => {
          window.location.hash = "#/insights";
        }}
      />
    );
  }

  if (activeCompanyPage) {
    return (
      <CompanyDetail
        page={activeCompanyPage}
        data={landingPageData}
        onHome={returnHome}
        onOpenPage={openCompanyPage}
      />
    );
  }

  if (activeProductPage === "agent-platform") {
    return (
      <AgentPlatformPage
        onHome={returnHome}
        contact={landingPageData.Contact}
      />
    );
  }

  if (activeProductPage === "insights") {
    return (
      <>
        <Navigation detailMode detailModeType="insights" onHome={returnHome} onOpenPage={openProductPage} />
        <InsightsPage
          items={insights}
          onOpenPage={openProductPage}
          onHome={returnHome}
          contact={landingPageData.Contact}
        />
        <Footer contact={landingPageData.Contact} onHome={returnHome} />
      </>
    );
  }

  if (activeProductPage?.startsWith("insights/")) {
    const article = insights.find(
      (item) => `insights/${item.slug}` === activeProductPage
    );
    if (article) {
      return (
        <InsightDetailPage
          item={article}
          onHome={returnHome}
          onOpenPage={openProductPage}
          contact={landingPageData.Contact}
        />
      );
    }
  }

  return (
    <div>
      <Navigation onHome={returnHome} />
      <Header data={landingPageData.Header} onOpenPage={openProductPage} />
      <Features data={landingPageData.Features} />
      <AgentShowcase onOpenPage={openProductPage} />
      <About data={landingPageData.About} />
      <Services data={landingPageData.Services} />
      <Gallery data={landingPageData.Gallery} />
      <Testimonials data={landingPageData.Testimonials} onOpenCase={openCase} />
      <GlobalPresence
        data={landingPageData.GlobalPresence}
        onOpenPage={openCompanyPage}
      />
      <Contact data={landingPageData.Contact} />
      <Footer contact={landingPageData.Contact} />
    </div>
  );
};

export default App;
